import { useState, useEffect } from 'react';
import { getOrganizations } from '../api/controllers/organizations';
import { getApplicationsForOrg } from '../api/controllers/applications';

interface ApplicationCredentials {
    applicationId: string | null;
    applicationSecret: string | null;
    isLoading: boolean;
}

// Cache credentials at module level so they persist across component instances
let cachedCredentials: { applicationId: string; applicationSecret: string } | null = null;
let loadingPromise: Promise<{ applicationId: string; applicationSecret: string } | null> | null = null;

/**
 * Hook to get valid application credentials (id and secret) for the currently authenticated user.
 * Will automatically load credentials from the first available organization and application.
 * Credentials are cached at module level so they only need to be loaded once per session.
 * 
 * @returns ApplicationCredentials with applicationId, applicationSecret, and loading state
 */
export function useApplicationCredentials(): ApplicationCredentials {
    const [credentials, setCredentials] = useState<ApplicationCredentials>({
        applicationId: cachedCredentials?.applicationId ?? null,
        applicationSecret: cachedCredentials?.applicationSecret ?? null,
        isLoading: !cachedCredentials
    });

    useEffect(() => {
        // If we already have cached credentials, use them
        if (cachedCredentials) {
            setCredentials({
                applicationId: cachedCredentials.applicationId,
                applicationSecret: cachedCredentials.applicationSecret,
                isLoading: false
            });
            return;
        }

        const loadCredentials = async () => {
            // If already loading, wait for that promise
            if (loadingPromise) {
                const result = await loadingPromise;
                if (result) {
                    setCredentials({
                        applicationId: result.applicationId,
                        applicationSecret: result.applicationSecret,
                        isLoading: false
                    });
                } else {
                    setCredentials({
                        applicationId: null,
                        applicationSecret: null,
                        isLoading: false
                    });
                }
                return;
            }

            // Start loading
            loadingPromise = (async () => {
                try {
                    // Get organizations for the current user
                    const orgsResult = await getOrganizations();
                    if (orgsResult.resultType !== 'Ok' || !orgsResult.data?.length) {
                        return null;
                    }

                    // Try each organization until we find one with applications
                    for (const org of orgsResult.data) {
                        const appsResult = await getApplicationsForOrg(org.id);
                        if (appsResult.resultType === 'Ok' && appsResult.data?.length > 0) {
                            const app = appsResult.data[0];
                            cachedCredentials = {
                                applicationId: app.id,
                                applicationSecret: app.secret
                            };
                            return cachedCredentials;
                        }
                    }
                    return null;
                } catch (error) {
                    console.error('Failed to load application credentials:', error);
                    return null;
                }
            })();

            const result = await loadingPromise;
            if (result) {
                setCredentials({
                    applicationId: result.applicationId,
                    applicationSecret: result.applicationSecret,
                    isLoading: false
                });
            } else {
                setCredentials({
                    applicationId: null,
                    applicationSecret: null,
                    isLoading: false
                });
            }
        };

        loadCredentials();
    }, []);

    return credentials;
}
